import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  LogOut,
  Trash2,
  LayoutDashboard,
  Mountain,
  ShoppingBag,
  Mail,
  Users,
  Lock,
  CalendarDays,
  Images,
  PenLine,
  Newspaper,
  PartyPopper,
  Plane,
  Trophy,
  Download,
} from "lucide-react";
import AdminCalendar from "@/pages/AdminCalendar";
import AdminArticles from "@/pages/AdminArticles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#FBF9F5]/60 border border-[#0D1117]/15 rounded-xl px-4 py-3 text-sm text-[#0D1117] placeholder:text-[#6B7280] focus:border-[#FF4E00]/60 focus:outline-none transition-all duration-300";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "challenges", label: "Challenges", icon: Mountain },
  { id: "events", label: "Events", icon: PartyPopper },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "travel", label: "Travel", icon: Plane },
  { id: "gallery", label: "Gear Room", icon: ShoppingBag },
  { id: "photos", label: "Finisher Wall", icon: Images },
  { id: "stories", label: "Stories", icon: PenLine },
  { id: "articles", label: "Articles", icon: Newspaper },
  { id: "results", label: "Results", icon: Trophy },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "participants", label: "Athletes", icon: Users },
];

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
      <div className="font-serif text-3xl text-[#FF4E00]">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mt-2">{label}</div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("fg_admin") || "");
  const [key, setKey] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoForm, setPhotoForm] = useState({ url: "", caption: "" });
  const [stories, setStories] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState({ title: "", date: "", city: "", status: "upcoming", description: "", reg_url: "" });
  const [trips, setTrips] = useState([]);
  const [tripForm, setTripForm] = useState({ race: "", city: "", country: "", dates: "", price: "", img: "", highlights: "" });
  const [challengesList, setChallengesList] = useState([]);
  const [resultsSlug, setResultsSlug] = useState("");
  const [resultsRows, setResultsRows] = useState([]);
  const [resultsFile, setResultsFile] = useState(null);
  const [challengeForm, setChallengeForm] = useState({ title: "", metric: "distance", goal: "", window_days: 30, description: "", start_date: "", end_date: "", distance_label: "", townscript_url: "" });
  const [collectionForm, setCollectionForm] = useState({ name: "", description: "" });
  const [productForm, setProductForm] = useState({ collection_id: "", name: "", description: "", price_text: "", image_url: "", affiliate_url: "" });
  const [saving, setSaving] = useState(false);

  const authed = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const handleError = useCallback((e, fallback) => {
    if (e?.response?.status === 401) {
      localStorage.removeItem("fg_admin");
      setToken("");
      toast.error("Session expired — sign in again");
    } else {
      toast.error(fallback);
    }
  }, []);

  const loadTab = useCallback(
    async (which) => {
      try {
        if (which === "overview") {
          const r = await axios.get(`${API}/admin/overview`, authed());
          setOverview(r.data);
        } else if (which === "challenges") {
          const r = await axios.get(`${API}/challenges`);
          setChallenges(r.data);
        } else if (which === "gallery") {
          const r = await axios.get(`${API}/gallery`);
          setGallery(r.data);
        } else if (which === "enquiries") {
          const r = await axios.get(`${API}/enquiries`, authed());
          setEnquiries(r.data);
          const rr = await axios.get(`${API}/admin/reminders`, authed());
          setReminders(rr.data);
        } else if (which === "stories") {
          const r = await axios.get(`${API}/admin/stories`, authed());
          setStories(r.data);
        } else if (which === "events") {
          const r = await axios.get(`${API}/events`);
          setEvents(r.data);
        } else if (which === "travel") {
          const r = await axios.get(`${API}/travel`);
          setTrips(r.data);
        } else if (which === "results") {
          const r = await axios.get(`${API}/challenges`);
          setChallengesList(r.data);
          if (resultsSlug) {
            const rr = await axios.get(`${API}/results/${resultsSlug}`);
            setResultsRows(rr.data);
          }
        } else if (which === "participants") {
          const r = await axios.get(`${API}/admin/participants`, authed());
          setParticipants(r.data);
        } else if (which === "photos") {
          const r = await axios.get(`${API}/photos`);
          setPhotos(r.data);
        }
      } catch (e) {
        handleError(e, "Could not load data");
      }
    },
    [authed, handleError, resultsSlug]
  );

  useEffect(() => {
    if (token) loadTab(tab);
  }, [token, tab, loadTab]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { key });
      localStorage.setItem("fg_admin", r.data.token);
      setToken(r.data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid admin key");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("fg_admin");
    setToken("");
  };

  const createChallenge = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/challenges`, { ...challengeForm, goal: Number(challengeForm.goal), window_days: Number(challengeForm.window_days) }, authed());
      toast.success("Challenge created");
      setChallengeForm({ title: "", metric: "distance", goal: "", window_days: 30, description: "", start_date: "", end_date: "", distance_label: "", townscript_url: "" });
      loadTab("challenges");
    } catch (e2) {
      handleError(e2, e2?.response?.data?.detail || "Could not create challenge");
    } finally {
      setSaving(false);
    }
  };

  const deleteChallenge = async (slug) => {
    try {
      await axios.delete(`${API}/admin/challenges/${slug}`, authed());
      toast.success("Challenge removed");
      loadTab("challenges");
    } catch (e) {
      handleError(e, "Could not delete challenge");
    }
  };

  const createCollection = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/collections`, collectionForm, authed());
      toast.success("Collection created");
      setCollectionForm({ name: "", description: "" });
      loadTab("gallery");
    } catch (e2) {
      handleError(e2, "Could not create collection");
    } finally {
      setSaving(false);
    }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/products`, productForm, authed());
      toast.success("Product added");
      setProductForm({ ...productForm, name: "", description: "", price_text: "", image_url: "", affiliate_url: "" });
      loadTab("gallery");
    } catch (e2) {
      handleError(e2, "Could not add product");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, authed());
      toast.success("Product removed");
      loadTab("gallery");
    } catch (e) {
      handleError(e, "Could not delete product");
    }
  };

  const deleteCollection = async (id) => {
    try {
      await axios.delete(`${API}/admin/collections/${id}`, authed());
      toast.success("Collection removed");
      loadTab("gallery");
    } catch (e) {
      handleError(e, "Could not delete collection");
    }
  };

  const createPhoto = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/photos`, photoForm, authed());
      toast.success("Photo added to the wall");
      setPhotoForm({ url: "", caption: "" });
      loadTab("photos");
    } catch (e2) {
      handleError(e2, e2?.response?.data?.detail || "Could not add photo");
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (id) => {
    try {
      await axios.delete(`${API}/admin/photos/${id}`, authed());
      toast.success("Photo removed");
      loadTab("photos");
    } catch (e) {
      handleError(e, "Could not delete photo");
    }
  };

  const deleteStory = async (id) => {
    try {
      await axios.delete(`${API}/admin/stories/${id}`, authed());
      toast.success("Story removed");
      loadTab("stories");
    } catch (e) {
      handleError(e, "Could not delete story");
    }
  };

  const deleteReminder = async (id) => {
    try {
      await axios.delete(`${API}/admin/reminders/${id}`, authed());
      toast.success("Reminder removed");
      loadTab("enquiries");
    } catch (e) {
      handleError(e, "Could not delete reminder");
    }
  };

  const deleteParticipant = async (id) => {
    try {
      await axios.delete(`${API}/admin/participants/${id}`, authed());
      toast.success("Athlete removed");
      loadTab("participants");
    } catch (e) {
      handleError(e, "Could not remove athlete");
    }
  };

  const cyclePayment = async (p) => {
    const order = ["pending", "paid", "waived"];
    const next = order[(order.indexOf(p.payment_status || "pending") + 1) % order.length];
    try {
      await axios.patch(`${API}/admin/participants/${p.id}/payment`, { status: next }, authed());
      toast.success(`Payment marked ${next}`);
      loadTab("participants");
    } catch (e) {
      handleError(e, "Could not update payment status");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/export/registrations.csv`, { ...authed(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "fitygrity-registrations.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registrations CSV downloaded");
    } catch (e) {
      handleError(e, "Export failed");
    }
  };

  const createEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/events`, eventForm, authed());
      toast.success("Event created");
      setEventForm({ title: "", date: "", city: "", status: "upcoming", description: "", reg_url: "" });
      loadTab("events");
    } catch (e2) {
      handleError(e2, "Could not create event");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/admin/events/${id}`, authed());
      toast.success("Event removed");
      loadTab("events");
    } catch (e) {
      handleError(e, "Could not delete event");
    }
  };

  const saveTrip = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (tripForm.id) {
        await axios.put(`${API}/admin/travel/${tripForm.id}`, tripForm, authed());
        toast.success("Trip updated");
      } else {
        await axios.post(`${API}/admin/travel`, tripForm, authed());
        toast.success("Trip added");
      }
      setTripForm({ race: "", city: "", country: "", dates: "", price: "", img: "", highlights: "" });
      loadTab("travel");
    } catch (e2) {
      handleError(e2, "Could not save trip");
    } finally {
      setSaving(false);
    }
  };

  const deleteTrip = async (id) => {
    try {
      await axios.delete(`${API}/admin/travel/${id}`, authed());
      toast.success("Trip removed");
      loadTab("travel");
    } catch (e) {
      handleError(e, "Could not delete trip");
    }
  };

  const uploadResults = async (e) => {
    e.preventDefault();
    if (!resultsFile) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", resultsFile);
      const r = await axios.post(`${API}/admin/results`, fd, authed());
      toast.success(`${r.data.imported} results imported`);
      setResultsFile(null);
      if (resultsSlug) loadResults(resultsSlug);
    } catch (e2) {
      handleError(e2, e2?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const loadResults = async (slug) => {
    setResultsSlug(slug);
    if (!slug) {
      setResultsRows([]);
      return;
    }
    try {
      const r = await axios.get(`${API}/results/${slug}`);
      setResultsRows(r.data);
    } catch {
      setResultsRows([]);
    }
  };

  const clearResults = async () => {
    if (!resultsSlug) return;
    try {
      await axios.delete(`${API}/admin/results/${resultsSlug}`, authed());
      toast.success("Results cleared");
      setResultsRows([]);
    } catch (e) {
      handleError(e, "Could not clear results");
    }
  };

  const publishStory = async (id, publish) => {    try {
      await axios.post(`${API}/admin/stories/${id}/${publish ? "publish" : "unpublish"}`, {}, authed());
      toast.success(publish ? "Story is live on the Journal" : "Story unpublished");
      loadTab("stories");
    } catch (e) {
      handleError(e, "Could not update story");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl border border-[#0D1117]/10 bg-[#FFFFFF]/80 backdrop-blur p-8 sm:p-10"
        >
          <span className="w-14 h-14 rounded-full border border-[#FF4E00]/30 flex items-center justify-center text-[#FF4E00]">
            <Lock className="w-6 h-6" />
          </span>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0D1117] tracking-tight mt-6">Race Control</h1>
          <p className="text-sm text-[#374151] mt-3">Enter the admin key to manage challenges, gear and athletes.</p>
          <input
            type="password"
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className={`${inputCls} mt-6`}
          />
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="mt-4 w-full inline-flex items-center justify-center gap-3 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 rounded-full transition-all duration-300"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
          </button>
          <Link to="/" className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#FF4E00] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-xl bg-[#FBF9F5]/75 border-b border-[#0D1117]/10">
        <nav className="px-6 sm:px-12 lg:px-20 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-horizontal.png" alt="Fitygrity" className="h-7 w-auto object-contain rounded-md" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mt-1">Race Control</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] hover:text-[#FF4E00] transition-colors">
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#374151] hover:text-red-600 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </nav>
      </header>

      <main className="px-6 sm:px-12 lg:px-20 pt-24 sm:pt-28 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] px-4 py-2.5 rounded-full border transition-all duration-300 ${
                tab === t.id
                  ? "bg-[#FF4E00] border-[#FF4E00] text-[#FBF9F5]"
                  : "border-[#0D1117]/15 text-[#374151] hover:border-[#FF4E00]/50 hover:text-[#FF4E00]"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && overview && (
          <div data-testid="admin-overview">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              <Stat label="Athletes" value={overview.participants} />
              <Stat label="Activities" value={overview.activities} />
              <Stat label="Verified" value={overview.verified} />
              <Stat label="Challenges" value={overview.challenges} />
              <Stat label="Enquiries" value={overview.enquiries} />
              <Stat label="Products" value={overview.products} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">Recent Enquiries</p>
                <ul className="space-y-3">
                  {overview.recent_enquiries.map((q) => (
                    <li key={q.id} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-[#0D1117] truncate">{q.name}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] shrink-0">{q.realm}</span>
                    </li>
                  ))}
                  {overview.recent_enquiries.length === 0 && <li className="text-sm text-[#6B7280]">No enquiries yet.</li>}
                </ul>
              </div>
              <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">Recent Synced Activities</p>
                <ul className="space-y-3">
                  {overview.recent_activities.map((a) => (
                    <li key={`${a.participant_id}-${a.strava_activity_id}`} className="flex items-center justify-between gap-4 text-sm">
                      <span className="text-[#0D1117] truncate">{a.participant_name} — {a.name}</span>
                      <span className={`font-mono text-[10px] uppercase tracking-[0.15em] shrink-0 ${a.status.startsWith("rejected") ? "text-red-600" : "text-emerald-600"}`}>
                        {a.status}
                      </span>
                    </li>
                  ))}
                  {overview.recent_activities.length === 0 && <li className="text-sm text-[#6B7280]">No activities synced yet.</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {tab === "challenges" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-challenges">
            <form onSubmit={createChallenge} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">New Challenge</p>
              <input required placeholder="Title — e.g. Monsoon 50K" value={challengeForm.title} onChange={(e) => setChallengeForm({ ...challengeForm, title: e.target.value })} className={inputCls} data-testid="admin-challenge-title" />
              <div className="grid grid-cols-3 gap-3">
                <select value={challengeForm.metric} onChange={(e) => setChallengeForm({ ...challengeForm, metric: e.target.value })} className={`${inputCls} appearance-none`} data-testid="admin-challenge-metric">
                  <option value="distance" className="bg-[#FFFFFF]">Distance (m)</option>
                  <option value="elevation" className="bg-[#FFFFFF]">Elevation (m)</option>
                </select>
                <input required type="number" min="1" placeholder="Goal" value={challengeForm.goal} onChange={(e) => setChallengeForm({ ...challengeForm, goal: e.target.value })} className={inputCls} data-testid="admin-challenge-goal" />
                <input required type="number" min="1" placeholder="Days" value={challengeForm.window_days} onChange={(e) => setChallengeForm({ ...challengeForm, window_days: e.target.value })} className={inputCls} data-testid="admin-challenge-days" />
              </div>
              <textarea rows={3} placeholder="Description" value={challengeForm.description} onChange={(e) => setChallengeForm({ ...challengeForm, description: e.target.value })} className={`${inputCls} resize-none`} data-testid="admin-challenge-description" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-[0.15em] text-[#6B7280] mb-1.5">Start date</label>
                  <input type="date" value={challengeForm.start_date} onChange={(e) => setChallengeForm({ ...challengeForm, start_date: e.target.value })} className={inputCls} data-testid="admin-challenge-start" />
                </div>
                <div>
                  <label className="block font-mono text-[9px] uppercase tracking-[0.15em] text-[#6B7280] mb-1.5">End date</label>
                  <input type="date" value={challengeForm.end_date} onChange={(e) => setChallengeForm({ ...challengeForm, end_date: e.target.value })} className={inputCls} data-testid="admin-challenge-end" />
                </div>
              </div>
              <input placeholder="Distance label — e.g. 50 km" value={challengeForm.distance_label} onChange={(e) => setChallengeForm({ ...challengeForm, distance_label: e.target.value })} className={inputCls} data-testid="admin-challenge-distance-label" />
              <input type="url" placeholder="Registration URL (event page, or /contact for register-interest)" value={challengeForm.townscript_url} onChange={(e) => setChallengeForm({ ...challengeForm, townscript_url: e.target.value })} className={inputCls} data-testid="admin-challenge-townscript" />
              <button type="submit" disabled={saving} data-testid="admin-challenge-create" className="w-full inline-flex items-center justify-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Challenge"}
              </button>
            </form>
            <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">Live Challenges</p>
              <ul className="space-y-4">
                {challenges.map((c) => (
                  <li key={c.slug} className="flex items-center justify-between gap-4 border-b border-[#0D1117]/5 pb-4">
                    <div>
                      <p className="text-sm text-[#0D1117]">{c.title}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-1">
                        {c.metric} · {c.goal.toLocaleString()} · {c.window_days}d
                      </p>
                    </div>
                    <button onClick={() => deleteChallenge(c.slug)} data-testid="admin-challenge-delete" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label={`Delete ${c.title}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === "gallery" && (
          <div className="space-y-6" data-testid="admin-gallery">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <form onSubmit={createCollection} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">New Collection</p>
                <input required placeholder="Name — e.g. Winter Layering" value={collectionForm.name} onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })} className={inputCls} data-testid="admin-collection-name" />
                <input placeholder="Description" value={collectionForm.description} onChange={(e) => setCollectionForm({ ...collectionForm, description: e.target.value })} className={inputCls} />
                <button type="submit" disabled={saving} data-testid="admin-collection-create" className="w-full inline-flex items-center justify-center bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Collection"}
                </button>
              </form>
              <form onSubmit={createProduct} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">New Product</p>
                <select required value={productForm.collection_id} onChange={(e) => setProductForm({ ...productForm, collection_id: e.target.value })} className={`${inputCls} appearance-none`} data-testid="admin-product-collection">
                  <option value="" className="bg-[#FFFFFF]">Choose collection</option>
                  {gallery.map((c) => (
                    <option key={c.id} value={c.id} className="bg-[#FFFFFF]">{c.name}</option>
                  ))}
                </select>
                <input required placeholder="Product name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className={inputCls} data-testid="admin-product-name" />
                <input placeholder="Description" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className={inputCls} />
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Price — ₹8,999" value={productForm.price_text} onChange={(e) => setProductForm({ ...productForm, price_text: e.target.value })} className={inputCls} />
                  <input placeholder="Image URL" value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} className={inputCls} />
                </div>
                <input required type="url" placeholder="Affiliate URL — https://amazon.in/..." value={productForm.affiliate_url} onChange={(e) => setProductForm({ ...productForm, affiliate_url: e.target.value })} className={inputCls} data-testid="admin-product-url" />
                <button type="submit" disabled={saving} data-testid="admin-product-create" className="w-full inline-flex items-center justify-center bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Product"}
                </button>
              </form>
            </div>
            <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">Collections & Products</p>
              {gallery.map((c) => (
                <div key={c.id} className="mb-6">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <p className="font-serif text-lg text-[#0D1117]">{c.name}</p>
                    <button onClick={() => deleteCollection(c.id)} data-testid="admin-collection-delete" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label={`Delete ${c.name}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <ul className="space-y-2">
                    {c.products.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-4 text-sm border-b border-[#0D1117]/5 pb-2">
                        <span className="text-[#0D1117] truncate">{p.name} <span className="text-[#FF4E00] font-mono text-xs ml-2">{p.price_text}</span></span>
                        <button onClick={() => deleteProduct(p.id)} data-testid="admin-product-delete" className="text-[#6B7280] hover:text-red-600 transition-colors shrink-0" aria-label={`Delete ${p.name}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                    {c.products.length === 0 && <li className="text-sm text-[#6B7280]">No products yet.</li>}
                  </ul>
                </div>
              ))}
              {gallery.length === 0 && <p className="text-sm text-[#6B7280]">No collections yet.</p>}
            </div>
          </div>
        )}

        {tab === "calendar" && <AdminCalendar token={token} />}

        {tab === "events" && (
          <div className="space-y-6" data-testid="admin-events">
            <form onSubmit={createEvent} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Create Event</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input required placeholder="Title — e.g. Sunrise 10K" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className={inputCls} data-testid="admin-event-title" />
                <input placeholder="Date — e.g. 12 Oct 2026" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className={inputCls} data-testid="admin-event-date" />
                <input placeholder="City" value={eventForm.city} onChange={(e) => setEventForm({ ...eventForm, city: e.target.value })} className={inputCls} data-testid="admin-event-city" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <select value={eventForm.status} onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })} className={`${inputCls} appearance-none`} data-testid="admin-event-status">
                  <option value="upcoming" className="bg-[#FFFFFF]">Upcoming</option>
                  <option value="past" className="bg-[#FFFFFF]">Past</option>
                </select>
                <input placeholder="Description" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className={inputCls} data-testid="admin-event-description" />
                <input placeholder="Registration link (or /contact)" value={eventForm.reg_url} onChange={(e) => setEventForm({ ...eventForm, reg_url: e.target.value })} className={inputCls} data-testid="admin-event-regurl" />
              </div>
              <button type="submit" disabled={saving} data-testid="admin-event-create" className="inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Event"}
              </button>
            </form>
            <div className="space-y-3">
              {events.map((ev) => (
                <div key={ev.id} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-5 flex items-center justify-between gap-4" data-testid="admin-event-row">
                  <div>
                    <p className="font-serif text-lg text-[#0D1117]">{ev.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-1">{ev.date} · {ev.city} · {ev.status}</p>
                  </div>
                  <button onClick={() => deleteEvent(ev.id)} data-testid="admin-event-delete" className="text-[#6B7280] hover:text-red-600 transition-colors shrink-0" aria-label="Delete event">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {events.length === 0 && <p className="text-sm text-[#6B7280]">No events yet.</p>}
            </div>
          </div>
        )}

        {tab === "travel" && (
          <div className="space-y-6" data-testid="admin-travel">
            <form onSubmit={saveTrip} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">{tripForm.id ? "Edit Trip" : "Add Trip"}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input required placeholder="Race — e.g. Berlin Marathon" value={tripForm.race} onChange={(e) => setTripForm({ ...tripForm, race: e.target.value })} className={inputCls} data-testid="admin-trip-race" />
                <input placeholder="City" value={tripForm.city} onChange={(e) => setTripForm({ ...tripForm, city: e.target.value })} className={inputCls} data-testid="admin-trip-city" />
                <input placeholder="Country" value={tripForm.country} onChange={(e) => setTripForm({ ...tripForm, country: e.target.value })} className={inputCls} data-testid="admin-trip-country" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <input placeholder="Dates — e.g. Race day 27 Sep 2026 · 3-night trip" value={tripForm.dates} onChange={(e) => setTripForm({ ...tripForm, dates: e.target.value })} className={inputCls} data-testid="admin-trip-dates" />
                <input placeholder="Price — e.g. ₹1,85,000" value={tripForm.price} onChange={(e) => setTripForm({ ...tripForm, price: e.target.value })} className={inputCls} data-testid="admin-trip-price" />
                <input type="url" placeholder="Image URL (https://...)" value={tripForm.img} onChange={(e) => setTripForm({ ...tripForm, img: e.target.value })} className={inputCls} data-testid="admin-trip-img" />
              </div>
              <textarea rows={2} placeholder="Highlights — one per line" value={tripForm.highlights} onChange={(e) => setTripForm({ ...tripForm, highlights: e.target.value })} className={`${inputCls} resize-none`} data-testid="admin-trip-highlights" />
              <div className="flex gap-3">
                <button type="submit" disabled={saving} data-testid="admin-trip-save" className="inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : tripForm.id ? "Update Trip" : "Add Trip"}
                </button>
                {tripForm.id && (
                  <button type="button" onClick={() => setTripForm({ race: "", city: "", country: "", dates: "", price: "", img: "", highlights: "" })} className="font-mono text-xs uppercase tracking-[0.2em] text-[#6B7280] hover:text-[#FF4E00] px-4">
                    Cancel
                  </button>
                )}
              </div>
            </form>
            <div className="space-y-3">
              {trips.map((t) => (
                <div key={t.id} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-5 flex items-center justify-between gap-4" data-testid="admin-trip-row">
                  <div>
                    <p className="font-serif text-lg text-[#0D1117]">{t.race}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B7280] mt-1">{t.city}{t.country ? `, ${t.country}` : ""} · {t.dates} · {t.price}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button onClick={() => setTripForm({ id: t.id, race: t.race, city: t.city, country: t.country, dates: t.dates, price: t.price, img: t.img, highlights: (t.highlights || []).join("\n") })} data-testid="admin-trip-edit" className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#FF4E00] hover:text-[#E04000]">
                      Edit
                    </button>
                    <button onClick={() => deleteTrip(t.id)} data-testid="admin-trip-delete" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label="Delete trip">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {trips.length === 0 && <p className="text-sm text-[#6B7280]">No trips yet.</p>}
            </div>
          </div>
        )}

        {tab === "results" && (
          <div className="space-y-6" data-testid="admin-results">
            <form onSubmit={uploadResults} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Upload Race Results (CSV)</p>
              <p className="text-xs text-[#6B7280]">Columns: challenge_slug, name, position, time. The challenge_slug must match an existing challenge.</p>
              <input type="file" accept=".csv" onChange={(e) => setResultsFile(e.target.files?.[0] || null)} className="block text-sm text-[#374151] file:mr-4 file:rounded-full file:border-0 file:bg-[#FF4E00]/10 file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.15em] file:text-[#FF4E00]" data-testid="admin-results-file" />
              <button type="submit" disabled={saving || !resultsFile} data-testid="admin-results-upload" className="inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload CSV"}
              </button>
            </form>
            <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-5">
                <select value={resultsSlug} onChange={(e) => loadResults(e.target.value)} className={`${inputCls} appearance-none sm:max-w-xs`} data-testid="admin-results-select">
                  <option value="" className="bg-[#FFFFFF]">Select challenge to view results</option>
                  {challengesList.map((c) => (
                    <option key={c.slug} value={c.slug} className="bg-[#FFFFFF]">{c.title}</option>
                  ))}
                </select>
                {resultsRows.length > 0 && (
                  <button onClick={clearResults} data-testid="admin-results-clear" className="font-mono text-[10px] uppercase tracking-[0.15em] text-red-600 hover:text-red-700">
                    Clear these results
                  </button>
                )}
              </div>
              <ul className="space-y-2">
                {resultsRows.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-4 border-b border-[#0D1117]/5 pb-2">
                    <span className="text-sm text-[#0D1117]">{r.position ? `#${r.position} ` : ""}{r.name}</span>
                    <span className="font-mono text-xs text-[#6B7280]">{r.time}</span>
                  </li>
                ))}
                {resultsSlug && resultsRows.length === 0 && <li className="text-sm text-[#6B7280]">No results uploaded for this challenge yet.</li>}
              </ul>
            </div>
          </div>
        )}

        {tab === "photos" && (
          <div className="space-y-6" data-testid="admin-photos">
            <form onSubmit={createPhoto} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 space-y-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Add Finish-Line Photo</p>
              <input
                required
                type="url"
                placeholder="Photo URL (https://... or /wall/file.webp)"
                value={photoForm.url}
                onChange={(e) => setPhotoForm({ ...photoForm, url: e.target.value })}
                className={inputCls}
                data-testid="photo-url-input"
              />
              <input
                placeholder="Caption — e.g. Priya's first 10K, Lodhi Gardens"
                value={photoForm.caption}
                onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })}
                className={inputCls}
                data-testid="photo-caption-input"
              />
              <button
                type="submit"
                disabled={saving}
                data-testid="photo-add-button"
                className="inline-flex items-center gap-2 bg-[#FF4E00] hover:bg-[#E04000] disabled:opacity-60 text-[#FBF9F5] font-mono text-xs uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Photo"}
              </button>
            </form>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {photos.map((p) => (
                <div key={p.id} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] overflow-hidden" data-testid="admin-photo-card">
                  <div className="aspect-[4/3] bg-[#F4F2EC]">
                    <img src={p.url} alt={p.caption || "Finisher photo"} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4 flex items-center justify-between gap-3">
                    <p className="text-sm text-[#374151] truncate">{p.caption || "—"}</p>
                    <button onClick={() => deletePhoto(p.id)} data-testid="photo-delete-button" className="text-[#6B7280] hover:text-red-600 transition-colors shrink-0" aria-label="Delete photo">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {photos.length === 0 && <p className="text-sm text-[#6B7280]">No photos yet — add the first finish-line moment.</p>}
            </div>
          </div>
        )}

        {tab === "stories" && (
          <div className="space-y-4" data-testid="admin-stories">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Journal Story Submissions</p>
            {stories.map((s) => (
              <div key={s.id} className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6 flex flex-col sm:flex-row gap-5" data-testid="admin-story-card">
                {s.image_path !== undefined && (
                  <img
                    src={`${API}/stories/${s.id}/image`}
                    alt={s.title}
                    className="w-full sm:w-36 h-28 object-cover rounded-xl bg-[#F4F2EC] shrink-0"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg text-[#0D1117]">{s.title}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#FF4E00] mt-1">{s.category} · {s.name} · {s.email}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`font-mono text-[9px] uppercase tracking-[0.15em] border rounded-full px-2.5 py-1 ${s.status === "published" ? "text-emerald-600 border-emerald-600/40" : "text-[#6B7280] border-[#0D1117]/15"}`}>
                        {s.status === "published" ? "Live" : "Pending"}
                      </span>
                      <button
                        onClick={() => publishStory(s.id, s.status !== "published")}
                        data-testid="story-publish-button"
                        className={`font-mono text-[10px] uppercase tracking-[0.12em] rounded-full px-4 py-2 transition-colors duration-300 ${s.status === "published" ? "border border-[#0D1117]/20 text-[#374151] hover:border-[#FF4E00]/60 hover:text-[#FF4E00]" : "bg-[#FF4E00] hover:bg-[#E04000] text-[#FBF9F5]"}`}
                      >
                        {s.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                      <button onClick={() => deleteStory(s.id)} data-testid="story-delete-button" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label="Delete story">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-[#374151] mt-3 leading-relaxed whitespace-pre-wrap">{s.story}</p>
                </div>
              </div>
            ))}
            {stories.length === 0 && <p className="text-sm text-[#6B7280]">No story submissions yet.</p>}
          </div>
        )}

        {tab === "articles" && <AdminArticles authed={authed} handleError={handleError} />}


        {tab === "enquiries" && (
          <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6" data-testid="admin-enquiries">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">All Enquiries</p>
            <ul className="space-y-4">
              {enquiries.map((q) => (
                <li key={q.id} className="border-b border-[#0D1117]/5 pb-4">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-[#0D1117]">{q.name} <span className="text-[#6B7280]">·</span> <span className="text-[#374151]">{q.email}</span>{q.phone && <span className="text-[#374151]"> · {q.phone}</span>}</p>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#FF4E00] shrink-0">{q.realm}</span>
                  </div>
                  {q.message && <p className="text-sm text-[#374151] mt-2">{q.message}</p>}
                </li>
              ))}
              {enquiries.length === 0 && <li className="text-sm text-[#6B7280]">No enquiries yet.</li>}
            </ul>
          </div>
        )}

        {tab === "enquiries" && (
          <div className="mt-6 rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6" data-testid="admin-reminders">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00] mb-5">Race-Day Reminder List ({reminders.length})</p>
            <ul className="space-y-3">
              {reminders.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-4 border-b border-[#0D1117]/5 pb-3">
                  <span className="text-sm text-[#0D1117]">{r.email}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#6B7280] text-right">{r.event_title}</span>
                    <button onClick={() => deleteReminder(r.id)} data-testid="reminder-delete-button" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label="Delete reminder">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </span>
                </li>
              ))}
              {reminders.length === 0 && <li className="text-sm text-[#6B7280]">No reminder signups yet.</li>}
            </ul>
          </div>
        )}

        {tab === "participants" && (
          <div className="rounded-2xl border border-[#0D1117]/10 bg-[#FFFFFF] p-6" data-testid="admin-participants">
            <div className="flex items-center justify-between gap-4 mb-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF4E00]">Registered Athletes</p>
              <button onClick={exportCsv} data-testid="export-csv-button" className="inline-flex items-center gap-2 border border-[#FF4E00]/50 hover:bg-[#FF4E00] hover:text-[#FBF9F5] text-[#FF4E00] font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-2 rounded-full transition-all duration-300">
                <Download className="w-3.5 h-3.5" /> Export Registrations CSV
              </button>
            </div>
            <ul className="space-y-4">
              {participants.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-4 border-b border-[#0D1117]/5 pb-4">
                  <div>
                    <p className="text-sm text-[#0D1117]">{p.name}</p>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#6B7280] mt-1">{p.email} · {p.challenge_slug}</p>
                    {p.profile && (
                      <div className="mt-2 font-mono text-[10px] tracking-[0.08em] text-[#374151] space-y-0.5" data-testid="participant-profile">
                        <p>{[p.profile.phone, p.profile.gender, p.profile.dob && `DOB ${p.profile.dob}`, p.profile.blood_group && `Blood ${p.profile.blood_group}`, p.profile.tshirt_size && `Tee ${p.profile.tshirt_size}`].filter(Boolean).join(" · ")}</p>
                        {(p.profile.address || p.profile.pincode) && <p className="text-[#6B7280]">{[p.profile.address, p.profile.pincode].filter(Boolean).join(", ")}</p>}
                        {p.profile.emergency_name && (
                          <p className="text-amber-700">SOS: {p.profile.emergency_name} · {p.profile.emergency_phone}{p.profile.emergency_relationship ? ` (${p.profile.emergency_relationship})` : ""}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => cyclePayment(p)}
                      data-testid="participant-payment-badge"
                      title="Tap to cycle payment status"
                      className={`font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-1 transition-colors duration-300 ${
                        (p.payment_status || "pending") === "paid"
                          ? "text-emerald-600 border-emerald-600/40"
                          : (p.payment_status) === "waived"
                            ? "text-sky-600 border-sky-600/40"
                            : "text-amber-700 border-amber-500/40"
                      }`}
                    >
                      {p.payment_status || "pending"}
                    </button>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${p.strava_connected ? "text-emerald-600 border-emerald-600/40" : "text-[#6B7280] border-[#0D1117]/15"}`}>
                      {p.strava_connected ? "Strava Linked" : "Not Linked"}
                    </span>
                    <button onClick={() => deleteParticipant(p.id)} data-testid="participant-delete-button" className="text-[#6B7280] hover:text-red-600 transition-colors" aria-label="Remove athlete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
              {participants.length === 0 && <li className="text-sm text-[#6B7280]">No athletes registered yet.</li>}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}
